import { useState } from 'react';
import { Table, Badge, Text, Group, Tooltip, UnstyledButton } from '@mantine/core';
import { plants, type PlantInfo } from '../data/plants';

function weekToDate(week: number): string {
  const d = new Date(2026, 0, 1);
  d.setDate(d.getDate() + (week - 1) * 7);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function rangeStr(range: [number, number] | null): string {
  if (!range) return '—';
  return `${weekToDate(range[0])} – ${weekToDate(range[1])}`;
}

const CATEGORY_TIPS: Record<string, string> = {
  vegetable: 'Edible plant grown for food — roots, leaves, stems, or fruits',
  herb: 'Aromatic plant used for flavoring, medicine, or pest control',
  fruit: 'Sweet or fleshy seed-bearing crop',
  flower: 'Grown for pollination, pest control, or companion benefits',
};

const LIFECYCLE_TIPS: Record<string, string> = {
  annual: 'Completes its life cycle in one growing season — replant each year',
  biennial: 'Takes two years to complete its life cycle — usually grown as an annual',
  perennial: 'Lives for multiple years — plant once and harvest for years to come',
};

function CategoryBadge({ plant }: { plant: PlantInfo }) {
  const colorMap: Record<string, string> = { herb: 'teal', fruit: 'grape', flower: 'pink', vegetable: 'blue' };
  return (
    <Tooltip label={CATEGORY_TIPS[plant.category]} withArrow multiline w={240}>
      <Badge size="xs" variant="light" color={colorMap[plant.category] || 'blue'}>{plant.category}</Badge>
    </Tooltip>
  );
}

type SortField = 'name' | 'category' | 'lifecycle' | 'indoorStart' | 'transplant' | 'directSow' | 'harvest' | 'maturity';
type SortDir = 'asc' | 'desc';

function getSortValue(plant: PlantInfo, field: SortField): number | string {
  switch (field) {
    case 'name': return plant.name.toLowerCase();
    case 'category': return plant.category;
    case 'lifecycle': return plant.lifecycle;
    case 'indoorStart': return plant.indoorStart?.[0] ?? 999;
    case 'transplant': return plant.transplant?.[0] ?? 999;
    case 'directSow': return plant.directSow?.[0] ?? 999;
    case 'harvest': return plant.harvestStart;
    case 'maturity': return plant.daysToMaturity[0];
  }
}

const stickyCol: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: 'var(--mantine-color-body)',
};

function SortableHeader({ label, field, sortField, sortDir, onSort, sticky }: {
  label: string; field: SortField; sortField: SortField; sortDir: SortDir;
  onSort: (field: SortField) => void; sticky?: boolean;
}) {
  const active = sortField === field;
  return (
    <Table.Th style={sticky ? { ...stickyCol, zIndex: 2 } : undefined}>
      <UnstyledButton onClick={() => onSort(field)} style={{ fontWeight: 600, fontSize: 'var(--mantine-font-size-sm)' }}>
        {label} {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      </UnstyledButton>
    </Table.Th>
  );
}

interface PlantTableProps {
  filter: string;
  isSelected: (name: string) => boolean;
}

export function PlantTable({ filter, isSelected }: PlantTableProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const onSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = plants
    .filter((p) => isSelected(p.name))
    .filter((p) => filter === 'all' || p.category === filter)
    .sort((a, b) => {
      const va = getSortValue(a, sortField);
      const vb = getSortValue(b, sortField);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <Table.ScrollContainer minWidth={1100}>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <SortableHeader label="Plant" field="name" sortField={sortField} sortDir={sortDir} onSort={onSort} sticky />
            <SortableHeader label="Type" field="category" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Lifecycle" field="lifecycle" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Start Indoors" field="indoorStart" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Transplant" field="transplant" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Direct Sow" field="directSow" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Harvest" field="harvest" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortableHeader label="Days" field="maturity" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <Table.Th>Companions</Table.Th>
            <Table.Th>Avoid</Table.Th>
            <Table.Th>Storage</Table.Th>
            <Table.Th>Notes</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map((plant) => (
            <Table.Tr key={plant.name}>
              <Table.Td style={stickyCol}>
                <Text fw={500} size="sm">{plant.name}</Text>
              </Table.Td>
              <Table.Td><CategoryBadge plant={plant} /></Table.Td>
              <Table.Td>
                <Tooltip label={LIFECYCLE_TIPS[plant.lifecycle]} withArrow multiline w={260}>
                  <Badge size="xs" variant="light" color={
                    plant.lifecycle === 'perennial' ? 'green' : plant.lifecycle === 'biennial' ? 'yellow' : 'gray'
                  }>{plant.lifecycle}</Badge>
                </Tooltip>
              </Table.Td>
              <Table.Td><Text size="xs">{rangeStr(plant.indoorStart)}</Text></Table.Td>
              <Table.Td><Text size="xs">{rangeStr(plant.transplant)}</Text></Table.Td>
              <Table.Td><Text size="xs">{rangeStr(plant.directSow)}</Text></Table.Td>
              <Table.Td>
                <Text size="xs">{weekToDate(plant.harvestStart)} – {weekToDate(plant.harvestEnd)}</Text>
              </Table.Td>
              <Table.Td><Text size="xs">{plant.daysToMaturity[0]}–{plant.daysToMaturity[1]}</Text></Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {plant.companions.slice(0, 3).map((c) => (
                    <Badge key={c} size="xs" variant="outline" color="green">{c}</Badge>
                  ))}
                  {plant.companions.length > 3 && (
                    <Tooltip label={plant.companions.slice(3).join(', ')} withArrow>
                      <Badge size="xs" variant="outline" color="gray">+{plant.companions.length - 3}</Badge>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {plant.avoid.length === 0 ? (
                    <Text size="xs" c="dimmed">—</Text>
                  ) : (
                    plant.avoid.map((a) => (
                      <Badge key={a} size="xs" variant="light" color="red">{a}</Badge>
                    ))
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Tooltip label={plant.storageNotes} withArrow multiline w={280}>
                  <Group gap={4}>
                    {plant.storage.slice(0, 2).map((s) => (
                      <Badge key={s} size="xs" variant="dot">{s}</Badge>
                    ))}
                    {plant.storage.length > 2 && (
                      <Badge size="xs" variant="outline" color="gray">+{plant.storage.length - 2}</Badge>
                    )}
                  </Group>
                </Tooltip>
              </Table.Td>
              <Table.Td>
                <Tooltip label={plant.notes} withArrow multiline w={300}>
                  <Text size="xs" lineClamp={1} style={{ maxWidth: 180 }}>{plant.notes}</Text>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
